import * as D from "./decimal.js";
import { serialToDate } from "./functions.js";
/** The colours Excel names in a format code. */
const COLOURS = {
    black: '#000000',
    blue: '#0000ff',
    cyan: '#00ffff',
    green: '#008000',
    magenta: '#ff00ff',
    red: '#ff0000',
    white: '#ffffff',
    yellow: '#ffff00',
};
const MONTHS_SHORT = [
    'Jan',
    'Feb',
    'Mar',
    'Apr',
    'May',
    'Jun',
    'Jul',
    'Aug',
    'Sep',
    'Oct',
    'Nov',
    'Dec',
];
const MONTHS_LONG = [
    'January',
    'February',
    'March',
    'April',
    'May',
    'June',
    'July',
    'August',
    'September',
    'October',
    'November',
    'December',
];
const DAYS_SHORT = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];
const DAYS_LONG = [
    'Sunday',
    'Monday',
    'Tuesday',
    'Wednesday',
    'Thursday',
    'Friday',
    'Saturday',
];
/**
 * Split a code on its semicolons, ignoring the ones inside quotes or
 * brackets.
 *
 * `[<=9999999]###-####;(###) ###-####` is two sections, and the semicolon
 * inside a quoted literal — `"a;b"0` — is not a split at all.
 */
export function splitSections(code) {
    const parts = [];
    let current = '';
    let quoted = false;
    let bracketed = false;
    for (let index = 0; index < code.length; index += 1) {
        const character = code[index];
        if (character === '"') {
            quoted = !quoted;
            current += character;
            continue;
        }
        if (!quoted && character === '[')
            bracketed = true;
        if (!quoted && character === ']')
            bracketed = false;
        if (character === '\\') {
            current += character + (code[index + 1] ?? '');
            index += 1;
            continue;
        }
        if (character === ';' && !quoted && !bracketed) {
            parts.push(current);
            current = '';
            continue;
        }
        current += character;
    }
    parts.push(current);
    return parts;
}
/** Pull the `[Red]` and `[<=100]` prefixes off a section. */
function readSection(raw) {
    let code = raw;
    let colour;
    let condition;
    for (;;) {
        const match = /^\[([^\]]+)\]/.exec(code);
        if (!match)
            break;
        const inside = (match[1] ?? '').trim();
        const named = COLOURS[inside.toLowerCase()];
        const test = /^(<=|>=|<>|=|<|>)(-?[\d.]+)$/.exec(inside);
        if (named)
            colour = named;
        else if (test)
            condition = { op: test[1], value: Number(test[2]) };
        // Anything else in brackets — a locale id, [h] elapsed hours — is left
        // alone rather than guessed at.
        else
            break;
        code = code.slice(match[0].length);
    }
    return { code, colour, condition };
}
/** Whether a section's condition admits this number. */
function admits(condition, value) {
    if (!condition)
        return true;
    switch (condition.op) {
        case '<':
            return value < condition.value;
        case '<=':
            return value <= condition.value;
        case '>':
            return value > condition.value;
        case '>=':
            return value >= condition.value;
        case '=':
            return value === condition.value;
        case '<>':
            return value !== condition.value;
        default:
            return true;
    }
}
/**
 * Whether a section describes a date rather than a number.
 *
 * `m` is a month in `d-mmm-yy` and a minute in `h:mm`, so the letter alone
 * settles nothing; what settles it is whether any *unambiguous* date or time
 * letter is present. `0.00` has none and is a number; `mmm-yy` has `y`.
 */
/** `[h]`, `[m]` or `[s]` — a duration rather than a time of day. */
function elapsedUnit(code) {
    const match = /\[(h+|m+|s+)\]/i.exec(code);
    if (!match)
        return null;
    return (match[1] ?? 'h')[0]?.toLowerCase();
}
function isDateCode(code) {
    if (elapsedUnit(code))
        return true;
    const bare = stripLiterals(code);
    if (/[ydhs]/i.test(bare))
        return true;
    // `mmm` and longer can only be a month name; there is no minute spelled
    // with three letters. `mmmmm` alone is Excel's "M" for March.
    if (/m{3,}/i.test(bare))
        return true;
    return /m/i.test(bare) && /[:/-]/.test(bare);
}
/** A code with its quoted and escaped literals removed. */
function stripLiterals(code) {
    return code.replace(/"[^"]*"/g, '').replace(/\\./g, '').replace(/\[[^\]]*\]/g, '');
}
/**
 * Render a value through a format code.
 *
 * Returns null when the code is one this does not understand, so the caller
 * can fall back to its named format rather than show something wrong.
 */
export function renderPattern(value, code) {
    if (!code.trim())
        return null;
    if (code.trim().toLowerCase() === 'general') {
        return { text: value.kind === 'number' ? D.toString(value.value) : plainText(value) };
    }
    const sections = splitSections(code).map(readSection);
    if (sections.length === 0)
        return null;
    if (value.kind === 'text') {
        // The fourth section is the text one; without it, text passes through.
        const textSection = sections[3];
        if (!textSection)
            return { text: value.value };
        return {
            text: textSection.code.replace(/@/g, value.value).replace(/"([^"]*)"/g, '$1'),
            colour: textSection.colour,
        };
    }
    if (value.kind !== 'number')
        return { text: plainText(value) };
    // A code that is nothing but the text placeholder shows the value as it was
    // typed. Excel's "@" is the Text category's own code, and a number given it
    // is left exactly as entered rather than formatted at all.
    // Literally "@" and nothing else — the Text category's own code. A code
    // with words around it, like `"Total "@`, keeps them and is drawn below.
    if (sections.length === 1 && (sections[0]?.code ?? '').trim() === '@') {
        return { text: D.toString(value.value), colour: sections[0]?.colour };
    }
    const chosen = chooseSection(sections, value.value);
    if (!chosen)
        return null;
    // Sections beyond the first drop the sign: the code supplies it, which is
    // how `0.00;(0.00)` writes a negative in brackets with no minus as well.
    const magnitude = chosen.signed ? value.value : D.abs(value.value);
    const rendered = isDateCode(chosen.section.code)
        ? renderDate(magnitude, chosen.section.code)
        : renderNumber(magnitude, chosen.section.code);
    if (rendered === null)
        return null;
    return { text: rendered, colour: chosen.section.colour };
}
/**
 * Which section applies, and whether it keeps the sign.
 *
 * Excel's rule, which is stranger than it looks: one section serves every
 * value and writes its own minus sign; two split at zero, with the second
 * taking the absolute value; three add a zero section; and a condition on a
 * section overrides all of that.
 */
function chooseSection(sections, value) {
    const asNumber = Number(D.toString(value));
    const conditional = sections.filter((section) => section.condition);
    if (conditional.length > 0) {
        for (const section of sections) {
            if (admits(section.condition, asNumber))
                return { section, signed: true };
        }
        return { section: sections[sections.length - 1], signed: true };
    }
    const negative = D.compare(value, D.fromInt(0)) < 0;
    const zero = D.isZero(value);
    if (sections.length === 1)
        return { section: sections[0], signed: true };
    if (zero && sections[2])
        return { section: sections[2], signed: false };
    if (negative && sections[1])
        return { section: sections[1], signed: false };
    return { section: sections[0], signed: true };
}
function plainText(value) {
    switch (value.kind) {
        case 'error':
            return value.code;
        case 'boolean':
            return value.value ? 'TRUE' : 'FALSE';
        case 'blank':
            return '';
        case 'text':
            return value.value;
        default:
            return D.toString(value.value);
    }
}
// --- dates -------------------------------------------------------------------
/**
 * A serial through a date code.
 *
 * The `m` question is settled the way Excel settles it: a run of `m` is
 * minutes when the nearest preceding token was hours or the nearest following
 * one is seconds, and months otherwise.
 */
function renderDate(value, code) {
    const serial = Number(D.toString(value));
    if (!Number.isFinite(serial) || serial < 0)
        return null;
    // An elapsed code counts past midnight rather than wrapping: [h]:mm:ss on
    // thirty hours is 30:00:00, which is the whole point of the brackets —
    // adding up a week of shifts gives a total, not a time of day.
    const elapsed = elapsedUnit(code);
    if (elapsed)
        return renderElapsed(serial, code, elapsed);
    const whole = Math.floor(serial);
    const date = whole >= 1 ? serialToDate(whole) : serialToDate(1);
    const dayFraction = serial - whole;
    const totalSeconds = Math.round(dayFraction * 86400);
    const within = ((totalSeconds % 86400) + 86400) % 86400;
    const hours24 = Math.floor(within / 3600);
    const minutes = Math.floor((within % 3600) / 60);
    const seconds = within % 60;
    const tokens = tokenise(code);
    const twelveHour = tokens.some((token) => /^(am\/pm|a\/p)$/i.test(token.text));
    const hours = twelveHour ? hours24 % 12 || 12 : hours24;
    let out = '';
    for (let index = 0; index < tokens.length; index += 1) {
        const token = tokens[index];
        if (token.literal) {
            out += token.text;
            continue;
        }
        const lower = token.text.toLowerCase();
        if (/^y+$/.test(lower)) {
            const year = whole >= 1 ? date.getUTCFullYear() : 1900;
            out += lower.length <= 2 ? `${year}`.slice(2) : `${year}`;
            continue;
        }
        if (/^d+$/.test(lower)) {
            const day = date.getUTCDate();
            if (lower.length === 1)
                out += `${day}`;
            else if (lower.length === 2)
                out += `${day}`.padStart(2, '0');
            else if (lower.length === 3)
                out += DAYS_SHORT[date.getUTCDay()] ?? '';
            else
                out += DAYS_LONG[date.getUTCDay()] ?? '';
            continue;
        }
        if (/^h+$/.test(lower)) {
            out += lower.length === 1 ? `${hours}` : `${hours}`.padStart(2, '0');
            continue;
        }
        if (/^s+$/.test(lower)) {
            out += lower.length === 1 ? `${seconds}` : `${seconds}`.padStart(2, '0');
            continue;
        }
        if (/^m+$/.test(lower)) {
            if (isMinute(tokens, index)) {
                out += lower.length === 1 ? `${minutes}` : `${minutes}`.padStart(2, '0');
                continue;
            }
            const month = date.getUTCMonth();
            if (lower.length === 1)
                out += `${month + 1}`;
            else if (lower.length === 2)
                out += `${month + 1}`.padStart(2, '0');
            else if (lower.length === 3)
                out += MONTHS_SHORT[month] ?? '';
            else if (lower.length === 4)
                out += MONTHS_LONG[month] ?? '';
            else
                out += (MONTHS_LONG[month] ?? '').slice(0, 1);
            continue;
        }
        if (/^(am\/pm|a\/p)$/i.test(token.text)) {
            const isAm = hours24 < 12;
            if (lower === 'a/p')
                out += isAm ? 'A' : 'P';
            else
                out += isAm ? 'AM' : 'PM';
            continue;
        }
        out += token.text;
    }
    return out;
}
/**
 * A duration, where the bracketed unit is not wrapped.
 *
 * Only the first unit runs over: `[h]:mm:ss` counts every hour and then the
 * minutes and seconds within the last one, exactly as a timesheet reads.
 */
function renderElapsed(serial, code, unit) {
    const totalSeconds = Math.round(serial * 86400);
    const totals = {
        h: Math.floor(totalSeconds / 3600),
        m: Math.floor(totalSeconds / 60),
        s: totalSeconds,
    };
    const width = (/\[(h+|m+|s+)\]/i.exec(code)?.[1] ?? 'h').length;
    let out = `${totals[unit]}`.padStart(width, '0');
    const rest = code.slice((code.indexOf(']') ?? 0) + 1);
    const minutes = Math.floor((totalSeconds % 3600) / 60);
    const seconds = totalSeconds % 60;
    for (const token of tokenise(rest)) {
        if (token.literal) {
            out += token.text;
            continue;
        }
        const lower = token.text.toLowerCase();
        if (/^m+$/.test(lower))
            out += `${minutes}`.padStart(lower.length, '0');
        else if (/^s+$/.test(lower))
            out += `${seconds}`.padStart(lower.length, '0');
        else
            out += token.text;
    }
    return out;
}
/** Whether this run of `m` means minutes rather than months. */
function isMinute(tokens, index) {
    for (let before = index - 1; before >= 0; before -= 1) {
        const token = tokens[before];
        if (token.literal)
            continue;
        if (/^h+$/i.test(token.text))
            return true;
        if (/^[ydm]+$/i.test(token.text))
            break;
    }
    for (let after = index + 1; after < tokens.length; after += 1) {
        const token = tokens[after];
        if (token.literal)
            continue;
        if (/^s+$/i.test(token.text))
            return true;
        if (/^[ydm]+$/i.test(token.text))
            break;
    }
    return false;
}
/** Break a date code into runs of the same letter, and literals. */
function tokenise(code) {
    const tokens = [];
    let index = 0;
    while (index < code.length) {
        const character = code[index];
        if (character === '"') {
            const end = code.indexOf('"', index + 1);
            tokens.push({ text: code.slice(index + 1, end === -1 ? code.length : end), literal: true });
            index = end === -1 ? code.length : end + 1;
            continue;
        }
        if (character === '\\') {
            tokens.push({ text: code[index + 1] ?? '', literal: true });
            index += 2;
            continue;
        }
        if (character === '[') {
            const end = code.indexOf(']', index);
            index = end === -1 ? code.length : end + 1;
            continue;
        }
        // AM/PM is one token, not three runs.
        const meridiem = /^(AM\/PM|A\/P)/i.exec(code.slice(index));
        if (meridiem) {
            tokens.push({ text: meridiem[0], literal: false });
            index += meridiem[0].length;
            continue;
        }
        if (/[ymdhs]/i.test(character)) {
            let run = character;
            while (code[index + run.length]?.toLowerCase() === character.toLowerCase()) {
                run += code[index + run.length];
            }
            tokens.push({ text: run, literal: false });
            index += run.length;
            continue;
        }
        tokens.push({ text: character, literal: true });
        index += 1;
    }
    return tokens;
}
// --- numbers -----------------------------------------------------------------
/** A number through a numeric code. */
function renderNumber(value, code) {
    const fraction = /(.*?)([#?0]+)\s*\/\s*([#?0]+|\d+)(.*)/.exec(stripQuotes(code));
    if (fraction)
        return renderFraction(value, fraction);
    if (/[Ee][+-]?[0#]+/.test(code))
        return renderScientific(value, code);
    const percent = (code.match(/%/g) ?? []).length;
    let working = value;
    for (let index = 0; index < percent; index += 1) {
        working = D.multiply(working, D.fromInt(100));
    }
    // A comma at the end of the digits scales by a thousand each time.
    const scaling = /[#?0](,+)(?![#?0])/.exec(code.replace(/"[^"]*"/g, ''));
    if (scaling) {
        for (let index = 0; index < (scaling[1] ?? '').length; index += 1) {
            // Dividing by a literal thousand cannot fail; the null is for a zero
            // divisor, which this is not.
            working = D.divide(working, D.fromInt(1000), 10) ?? working;
        }
    }
    if (!/[#?0]/.test(code.replace(/"[^"]*"/g, ''))) {
        // No digit placeholders. If the code has a text placeholder it wants the
        // value written out — `"Total "@` — and otherwise it is pure literal,
        // like the dash a ledger shows for a zero.
        const bare = literal(code);
        return bare.includes('@') ? bare.split('@').join(D.toString(value)) : bare;
    }
    const { integerCode, decimalCode } = splitAtPoint(code);
    const decimals = (decimalCode.match(/[#?0]/g) ?? []).length;
    const negative = D.compare(working, D.fromInt(0)) < 0;
    const rounded = D.round(D.abs(working), decimals);
    const [wholeRaw = '0', fractionRaw = ''] = D.toString(rounded).split('.');
    const whole = fillIntegers(wholeRaw, integerCode);
    const decimalText = padDecimals(fractionRaw, decimalCode.replace(/[^#?0]/g, ''));
    const tail = literal(decimalCode.replace(/[#?0]/g, ''));
    return `${negative ? '-' : ''}${whole}${decimalText ? `.${decimalText}` : ''}${tail}`;
}
/**
 * Split a code at the decimal point, if it has one.
 *
 * The point has to be one between digits — `0.00` — rather than one inside a
 * quoted literal or a date, so the search ignores quoted runs.
 */
function splitAtPoint(code) {
    const masked = code.replace(/"[^"]*"/g, (match) => '\u0001'.repeat(match.length));
    const at = masked.indexOf('.');
    if (at === -1)
        return { integerCode: code, decimalCode: '' };
    return { integerCode: code.slice(0, at), decimalCode: code.slice(at + 1) };
}
/**
 * Lay the digits before the point into the code's placeholders.
 *
 * Walked from the right, because that is where a number lines up: `00000-0000`
 * puts the last four digits after the dash and the first five before it, and
 * `000-00-0000` splits nine digits three ways. Filling left to right, or
 * treating the first run of noughts as the whole field and the rest as text,
 * gives "980526399-0000" — which is what this did before.
 *
 * Digits left over when the placeholders run out are not dropped: they pile
 * up at the leftmost one, so `#,##0` still shows a million in full.
 */
function fillIntegers(digits, code) {
    const grouped = /[#?0],[#?0]/.test(code);
    const slots = readSlots(code);
    const firstDigit = slots.findIndex((slot) => slot.kind === 'digit');
    /*
     * A code that groups in thousands is a single field with a literal on
     * either side — "Rs "#,##0 or (#,##0) — never digits split across a
     * separator the way a ZIP+4 is. So the digits are grouped as one string
     * and the literals put back around them, rather than being laid into the
     * placeholders one at a time: laying them out individually would group only
     * whatever spilled past the last placeholder, giving 1,234567.
     */
    if (grouped) {
        const required = slots.filter((slot) => slot.kind === 'digit' && slot.text === '0').length;
        const body = (digits === '0' && required === 0 ? '' : digits)
            .padStart(required, '0')
            .replace(/\B(?=(\d{3})+(?!\d))/g, ',');
        const before = slots.slice(0, firstDigit === -1 ? 0 : firstDigit);
        const lastDigit = slots.map((slot) => slot.kind).lastIndexOf('digit');
        const after = slots.slice(lastDigit + 1);
        return `${before.map((slot) => slot.text).join('')}${body}${after.map((slot) => slot.text).join('')}`;
    }
    let remaining = digits === '0' ? '' : digits;
    const out = [];
    for (let index = slots.length - 1; index >= 0; index -= 1) {
        const slot = slots[index];
        if (slot.kind === 'text') {
            // A literal to the right of every digit — the ")" of a bracketed
            // negative — is written; so is one between digits, like a ZIP's dash.
            out.push(slot.text);
            continue;
        }
        // The leftmost placeholder takes everything still unplaced, so a code
        // with four of them still shows a million in full.
        if (index === firstDigit) {
            if (remaining.length > 0) {
                let head = remaining;
                if (grouped)
                    head = head.replace(/\B(?=(\d{3})+(?!\d))/g, ',');
                out.push(head);
                remaining = '';
            }
            else if (slot.text === '0')
                out.push('0');
            else if (slot.text === '?')
                out.push(' ');
            continue;
        }
        if (remaining.length > 0) {
            out.push(remaining.slice(-1));
            remaining = remaining.slice(0, -1);
        }
        else if (slot.text === '0')
            out.push('0');
        else if (slot.text === '?')
            out.push(' ');
    }
    return out.reverse().join('');
}
/**
 * A code's integer half, as placeholders and literal runs.
 *
 * Done as a pass rather than character by character because two of the
 * characters are not themselves: a quoted run is one literal however long it
 * is, and a comma is a marker — grouping when it sits between placeholders,
 * a thousand-scale when it trails them — rather than something to write.
 */
function readSlots(code) {
    const slots = [];
    let index = 0;
    while (index < code.length) {
        const character = code[index];
        if (character === '"') {
            const end = code.indexOf('"', index + 1);
            slots.push({ kind: 'text', text: code.slice(index + 1, end === -1 ? code.length : end) });
            index = end === -1 ? code.length : end + 1;
            continue;
        }
        if (character === '\\') {
            slots.push({ kind: 'text', text: code[index + 1] ?? '' });
            index += 2;
            continue;
        }
        // `_x` reserves the width of x; `*x` repeats x to fill. Both become a
        // space and nothing respectively, which is as close as a proportional
        // font gets to either.
        if (character === '_') {
            slots.push({ kind: 'text', text: ' ' });
            index += 2;
            continue;
        }
        if (character === '*') {
            index += 2;
            continue;
        }
        if (character === '[') {
            const end = code.indexOf(']', index);
            index = end === -1 ? code.length : end + 1;
            continue;
        }
        if (character === ',') {
            // Grouping or scaling; either way it is a marker, not a character.
            index += 1;
            continue;
        }
        if (character === '0' || character === '#' || character === '?') {
            slots.push({ kind: 'digit', text: character });
            index += 1;
            continue;
        }
        slots.push({ kind: 'text', text: character });
        index += 1;
    }
    return slots;
}
function padDecimals(digits, code) {
    const places = (code.match(/[#?0]/g) ?? []).length;
    if (places === 0)
        return '';
    let out = digits.padEnd(places, '\u0000').slice(0, places);
    // A `#` drops a trailing zero; a `0` keeps it; a `?` leaves a space.
    let result = '';
    for (let index = 0; index < places; index += 1) {
        const slot = code.replace(/[^#?0]/g, '')[index] ?? '0';
        const digit = out[index] === '\u0000' ? '' : out[index];
        if (digit)
            result += digit;
        else if (slot === '0')
            result += '0';
        else if (slot === '?')
            result += ' ';
    }
    // Trailing zeros a `#` did not require come off.
    out = result;
    const trailing = code.replace(/[^#?0]/g, '');
    while (out.length > 0 && trailing[out.length - 1] === '#' && out.endsWith('0')) {
        out = out.slice(0, -1);
    }
    return out;
}
/** `# ?/?` and `# ?/16` — a whole part and a vulgar fraction. */
function renderFraction(value, match) {
    const numeratorCode = match[2] ?? '?';
    const denominatorCode = match[3] ?? '?';
    const wholeCode = match[1] ?? '';
    const asNumber = Number(D.toString(value));
    const negative = asNumber < 0;
    const magnitude = Math.abs(asNumber);
    // A code with digits before the slash keeps the whole number separate; one
    // without it writes the whole thing as a top-heavy fraction.
    const showsWhole = /[#?0]/.test(wholeCode);
    const whole = showsWhole ? Math.floor(magnitude) : 0;
    const part = magnitude - whole;
    const fixed = /^\d+$/.test(denominatorCode) ? Number(denominatorCode) : 0;
    const limit = fixed > 0 ? fixed : 10 ** denominatorCode.length - 1;
    const { numerator, denominator } = fixed
        ? { numerator: Math.round(part * fixed), denominator: fixed }
        : bestFraction(part, limit);
    const sign = negative ? '-' : '';
    if (numerator === 0)
        return `${sign}${whole || 0}`;
    if (numerator === denominator)
        return `${sign}${whole + 1}`;
    // A '?' holds the width so a column of fractions lines up on its slash:
    // the numerator is pushed right against it and the denominator left, which
    // is the whole reason Excel has the placeholder.
    const body = `${numeratorCode.includes('?') ? `${numerator}`.padStart(numeratorCode.length, ' ') : numerator}` +
        '/' +
        `${denominatorCode.includes('?') ? `${denominator}`.padEnd(denominatorCode.length, ' ') : denominator}`;
    return showsWhole && whole > 0 ? `${sign}${whole} ${body}` : `${sign}${body}`;
}
/**
 * The closest fraction with a denominator no larger than `limit`.
 *
 * Walks the Stern–Brocot tree, which is exact and needs no search over every
 * denominator — and unlike rounding to a fixed one it will not write 0.333 as
 * 3/10 when 1/3 is available and closer.
 */
export function bestFraction(part, limit) {
    if (part <= 0)
        return { numerator: 0, denominator: 1 };
    let lowN = 0;
    let lowD = 1;
    let highN = 1;
    let highD = 1;
    let bestN = 1;
    let bestD = 1;
    for (;;) {
        const mediantN = lowN + highN;
        const mediantD = lowD + highD;
        if (mediantD > limit)
            break;
        bestN = mediantN;
        bestD = mediantD;
        if (part < mediantN / mediantD) {
            highN = mediantN;
            highD = mediantD;
        }
        else {
            lowN = mediantN;
            lowD = mediantD;
        }
    }
    for (const [n, d] of [
        [lowN, lowD],
        [highN, highD],
    ]) {
        if (d >= 1 && d <= limit && Math.abs(part - n / d) < Math.abs(part - bestN / bestD)) {
            bestN = n;
            bestD = d;
        }
    }
    return { numerator: bestN, denominator: bestD };
}
/**
 * Scientific, and its engineering cousin.
 *
 * The count of integer placeholders before the point is not decoration: one
 * (`0.00E+00`) is ordinary scientific, where the mantissa sits between 1 and
 * 10. Three (`##0.0E+0`) is engineering, where the exponent is held to a
 * multiple of three and the mantissa runs up to 1000 — which is how a
 * quantity is read off in thousands, millions and billions rather than in
 * whatever power happens to fall out.
 */
function renderScientific(value, code) {
    const match = /([#?0.,]*)[Ee]([+-])([0#]+)/.exec(code);
    if (!match)
        return null;
    const mantissaCode = match[1] ?? '0';
    const [integerCode = '', decimalCode = ''] = mantissaCode.split('.');
    const places = (decimalCode.match(/[#?0]/g) ?? []).length;
    const step = Math.max(1, (integerCode.match(/[#?0]/g) ?? []).length);
    const asNumber = Number(D.toString(value));
    if (!Number.isFinite(asNumber))
        return null;
    if (asNumber === 0) {
        return `${(0).toFixed(places)}E${match[2] === '+' ? '+' : ''}${'0'.padStart((match[3] ?? '00').length, '0')}`;
    }
    // Round the exponent down to the step, then move the mantissa to match.
    const raw = Math.floor(Math.log10(Math.abs(asNumber)));
    const exponent = Math.floor(raw / step) * step;
    const mantissa = (asNumber / 10 ** exponent).toFixed(places);
    const sign = exponent < 0 ? '-' : match[2] === '+' ? '+' : '';
    const width = (match[3] ?? '00').length;
    const digits = `${Math.abs(exponent)}`.padStart(width, '0');
    return `${mantissa}E${sign}${digits}`;
}
/** Literal run: quotes come off, `_x` is a gap, `*x` is a fill we do not do. */
function literal(code) {
    return code
        .replace(/"([^"]*)"/g, '$1')
        .replace(/\\(.)/g, '$1')
        .replace(/_./g, ' ')
        .replace(/\*./g, '')
        .replace(/[[\]]/g, '');
}
function stripQuotes(code) {
    return code.replace(/"([^"]*)"/g, '$1');
}
//# sourceMappingURL=number-pattern.js.map